import { startOfDay } from "date-fns/startOfDay";
import type { EnduserObservation } from "@tellescope/types-models";
import { lbToDisplayWeight, mlToOz, type WeightDisplayUnit } from "../../../utils/nutritionUnitConversions";

export const isSameDay = (a: Date, b: Date): boolean => a.toDateString() === b.toDateString();

/** Sum of a macro across a set of food-log components (or the top-level measurement, when the type has no components). */
export const sumMacro = (observations: EnduserObservation[], componentLabel?: string): number =>
  observations.reduce((sum, obs) => {
    if (!componentLabel) return sum + obs.measurement.value;
    const component = obs.components?.find((c) => c.code.text === componentLabel);
    return sum + (component?.valueQuantity.value ?? 0);
  }, 0);

export type MetricId = "weight" | "calories" | "protein" | "carbs" | "fat" | "fiber" | "water";

export interface MetricDef {
  id: MetricId;
  /** Display unit. For "weight" this is a placeholder — the real unit is resolved per-call via weightUnit. */
  unit: string;
  decimals: number;
  /** "weight" = one point per weigh-in; "sum" = daily total across entries. */
  kind: "weight" | "sum";
}

// No display label lives here — every render site gets the translated label from
// useMetricLabel(metricId) instead, since this file has no React/translation context of its own
// and a plain string field here would be too easy to render directly, bypassing i18n.
export const METRICS: Record<MetricId, MetricDef> = {
  weight: { id: "weight", unit: "lb", decimals: 1, kind: "weight" },
  calories: { id: "calories", unit: "kcal", decimals: 0, kind: "sum" },
  protein: { id: "protein", unit: "g", decimals: 0, kind: "sum" },
  carbs: { id: "carbs", unit: "g", decimals: 0, kind: "sum" },
  fat: { id: "fat", unit: "g", decimals: 0, kind: "sum" },
  fiber: { id: "fiber", unit: "g", decimals: 0, kind: "sum" },
  water: { id: "water", unit: "oz", decimals: 0, kind: "sum" },
};

export const METRIC_ORDER: MetricId[] = ["weight", "calories", "protein", "carbs", "fat", "fiber", "water"];
export const MACRO_IDS: MetricId[] = ["protein", "carbs", "fat", "fiber"];

/** Fixed per-macro colors, shared between the week overview bar chart and each macro's own trend card sparkline. */
export const MACRO_COLORS: Partial<Record<MetricId, string>> = {
  protein: "#a78bfa",
  carbs: "#60a5fa",
  fat: "#fbbf24",
  fiber: "#34d399",
};

// Deliberately NOT translated — these are internal EnduserObservation.components[].code.text
// storage keys, written verbatim in English by useWeightMacroTrackerBlock.ts's logFood at write
// time, and matched by exact string equality in sumMacro. They are never rendered; the actual
// on-screen labels are translated separately at each render site via t("Protein") etc.
// Translating this map would break matching against already-written English-keyed data.
/** Maps a macro MetricId to the exact EnduserObservation component `code.text` label used at write time. */
const MACRO_COMPONENT_LABEL: Partial<Record<MetricId, string>> = {
  protein: "Protein",
  carbs: "Carbohydrate",
  fat: "Fat",
  fiber: "Fiber",
};

export interface SeriesPoint {
  /** ISO day (start-of-day) for "sum" metrics; the raw entry timestamp for "weight" metrics. */
  date: string;
  value: number;
  /** Only populated for "weight" points, which are 1:1 with a single EnduserObservation. */
  entryId?: string;
}

export const round = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/** Reads "the number" for one metric off one observation, reusing the hook's tested sumMacro. Unrounded — callers round at their own display boundary. */
export const metricValueForObservation = (metric: MetricDef, obs: EnduserObservation): number => {
  if (metric.id === "water") return mlToOz(sumMacro([obs]));
  return sumMacro([obs], MACRO_COMPONENT_LABEL[metric.id]);
};

export const sourceEntriesFor = (
  metric: MetricDef,
  weightEntries: EnduserObservation[],
  foodEntries: EnduserObservation[],
  waterEntries: EnduserObservation[]
): EnduserObservation[] => {
  if (metric.kind === "weight") return weightEntries;
  if (metric.id === "water") return waterEntries;
  return foodEntries;
};

/** Builds a time series for one metric: one point per weigh-in, or one point per day (summed) for macros/water. */
export const buildSeries = (
  metric: MetricDef,
  weightEntries: EnduserObservation[],
  foodEntries: EnduserObservation[],
  waterEntries: EnduserObservation[],
  weightUnit: WeightDisplayUnit
): SeriesPoint[] => {
  if (metric.kind === "weight") {
    return [...weightEntries]
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .map((obs) => ({
        date: startOfDay(new Date(obs.timestamp)).toISOString(),
        value: round(lbToDisplayWeight(obs.measurement.value, weightUnit), metric.decimals),
        entryId: obs.id,
      }));
  }

  const source = sourceEntriesFor(metric, weightEntries, foodEntries, waterEntries);
  const byDay = new Map<string, number>();
  for (const obs of source) {
    const key = startOfDay(new Date(obs.timestamp)).toISOString();
    byDay.set(key, (byDay.get(key) ?? 0) + metricValueForObservation(metric, obs));
  }
  return [...byDay.entries()]
    .map(([date, total]) => ({ date, value: round(total, metric.decimals) }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

/** Today's value for a metric: latest weigh-in ever logged, or the sum of today's entries. */
export const todayValue = (
  metric: MetricDef,
  weightEntries: EnduserObservation[],
  foodEntries: EnduserObservation[],
  waterEntries: EnduserObservation[],
  weightUnit: WeightDisplayUnit
): number | null => {
  if (metric.kind === "weight") {
    const latest = [...weightEntries].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];
    if (!latest) return null;
    return round(lbToDisplayWeight(latest.measurement.value, weightUnit), metric.decimals);
  }

  const source = sourceEntriesFor(metric, weightEntries, foodEntries, waterEntries);
  const now = new Date();
  const total = source.reduce(
    (sum, obs) => (isSameDay(new Date(obs.timestamp), now) ? sum + metricValueForObservation(metric, obs) : sum),
    0
  );
  return round(total, metric.decimals);
};

/** All raw observations logged for a metric on a given ISO day. */
export const getEntriesForDate = (
  metric: MetricDef,
  date: string,
  weightEntries: EnduserObservation[],
  foodEntries: EnduserObservation[],
  waterEntries: EnduserObservation[]
): EnduserObservation[] => {
  const targetDate = startOfDay(new Date(date));
  const source = sourceEntriesFor(metric, weightEntries, foodEntries, waterEntries);
  return source.filter((obs) => isSameDay(new Date(obs.timestamp), targetDate));
};
